interface FontConfig {
  family: string;
  url: string;
  descriptors?: FontFaceDescriptors;
}

class FontLoader {
  private static instance: FontLoader;
  private loadedFonts: Set<string> = new Set();
  private loadingPromises: Map<string, Promise<void>> = new Map();

  static getInstance(): FontLoader {
    if (!FontLoader.instance) {
      FontLoader.instance = new FontLoader();
    }
    return FontLoader.instance;
  }

  async loadFont(config: FontConfig): Promise<void> {
    const { family, url, descriptors = {} } = config;
    
    // 如果已經載入，直接返回
    if (this.loadedFonts.has(family)) {
      return Promise.resolve();
    }

    // 如果正在載入，返回現有的 Promise
    if (this.loadingPromises.has(family)) {
      return this.loadingPromises.get(family)!;
    }

    // 創建載入 Promise
    const loadingPromise = this.createLoadingPromise(family, url, descriptors);
    this.loadingPromises.set(family, loadingPromise);

    return loadingPromise;
  }

  private async createLoadingPromise(
    family: string, 
    url: string, 
    descriptors: FontFaceDescriptors
  ): Promise<void> {
    try {
      // 檢查瀏覽器是否支援 FontFace
      if ('FontFace' in window) {
        const fontFace = new FontFace(family, `url(${url})`, {
          display: 'swap',
          ...descriptors
        });

        // 載入字體
        await fontFace.load();
        
        // 添加到文檔 - 使用型別斷言修復 TypeScript 錯誤
        (document.fonts as any).add(fontFace);
        
        this.loadedFonts.add(family);
        // console.log(`字體 ${family} 載入完成`);
      } else {
        // 降級方案：使用 CSS
        this.loadFontFallback(family, url, descriptors);
      }
    } catch (error) {
      console.warn(`字體 ${family} 載入失敗:`, error);
    } finally {
      this.loadingPromises.delete(family);
    }
  }

  private loadFontFallback(
    family: string, 
    url: string, 
    descriptors: FontFaceDescriptors
  ): void {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: "${family}";
        src: url("${url}");
        font-display: swap;
        ${descriptors.weight ? `font-weight: ${descriptors.weight};` : ''}
        ${descriptors.style ? `font-style: ${descriptors.style};` : ''}
      }
    `;
    document.head.appendChild(style);
    this.loadedFonts.add(family);
  }

  // 批量載入字體
  async loadFonts(configs: FontConfig[]): Promise<void> {
    const promises = configs.map(config => this.loadFont(config));
    await Promise.allSettled(promises);
  }

  // 檢查字體是否已載入
  isFontLoaded(family: string): boolean {
    return this.loadedFonts.has(family);
  }
}

// 字體配置
export const FONT_CONFIGS: FontConfig[] = [
  {
    family: 'msheij',
    url: './leading/leadingPageAssets/fonts/msheij.ttf'
  },
  {
    family: 'Microsoft_JhengHei',
    url: './leading/leadingPageAssets/fonts/Microsoft_JhengHei_Bold.ttf'
  }
];

export default FontLoader;